import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { KeyValue } from '@angular/common';
import { EMPTY, catchError } from 'rxjs';
import { IAsset } from 'src/app/models/products/asset';
import { IAssetLabel } from 'src/app/models/assetLabel';
import { AssetsService } from 'src/app/services/assets.service';
import { ProductsService } from 'src/app/services/products.service';
import { AlertService } from 'src/app/services/alert.service';
import {
  DynamicTableDialogComponent,
  TableComponentProps,
  TableHeader,
} from '../../../shared/dynamic-table/dynamic-table.component';
import { LabelEditorComponent } from '../label-editor/label-editor.component';

@Component({
  selector: 'app-label-display',
  templateUrl: './label-display.component.html',
  styleUrls: ['./label-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class LabelDisplayComponent implements OnChanges {
  @Input() assetDetail!: IAsset;
  @Input() assetType = '';

  contentExtracting = false;

  assetLabel: IAssetLabel | null = null;
  assetLabelVersions: IAssetLabel[] = [];
  assetLabelResultData: MatTableDataSource<KeyValue<string, any>> = new MatTableDataSource<
    KeyValue<string, any>
  >();
  activeAssetLabelTab = 0;
  loading = false;

  constructor(
    private assetsService: AssetsService,
    private productService: ProductsService,
    private alertService: AlertService,
    public dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assetDetail'] && this.assetDetail) {
      this.loadAssetLabel();
    }
  }

  loadAssetLabel(): void {
    this.loading = true;
    this.assetLabelResultData = new MatTableDataSource<KeyValue<string, any>>([]);
    this.assetsService.getAssetLabel(this.assetDetail.id, this.assetType).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.loading = false;
          this.assetLabel = null;
          this.assetLabelVersions = [];
        }
        return EMPTY;
      }),
    ).subscribe((res) => {
      this.assetLabelVersions = res;
      if (res == null) {
        this.loading = false;
        this.assetLabel = null;
        return;
      }
      this.assetLabel = res[0];
      this.parseContentsToTable(res[0]);
    });
  }

  onLabelTabChange(event: number): void {
    this.parseContentsToTable(this.assetLabelVersions[event]);
    this.activeAssetLabelTab = event;
  }

  parseContentsToTable(current: IAssetLabel): void {
    if (current.label == null) {
      this.loading = false;
      this.assetLabelResultData = new MatTableDataSource<KeyValue<string, any>>();
      return;
    }
    const result = Object.keys(current.label)
      .filter((key) => current.label[key] != null)
      .map((key) => {
        if (
          key === 'additionalInfo' ||
          key === 'packaging' ||
          key === 'ingredients' ||
          key === 'nutritionFacts'
        ) {
          const value = Object.keys(current.label[key])
            .filter((key2) => current.label[key][key2] != null)
            .map((key2) => ({
              key: key2,
              value: current.label[key][key2],
            }));
          return { key, value };
        }
        if ((key === 'marketingCopy' || key === 'regulatoryInfo') && current.label[key] != null) {
          const values: any[] = [];
          current.label[key].forEach((item: any) => {
            const value = Object.keys(item)
              .filter((itemKey) => item[itemKey] != null)
              .map((itemKey) => ({ key: itemKey, value: item[itemKey] }));
            values.push(value);
          });
          return { key, value: values };
        }
        return { key, value: current.label[key] };
      });
    this.assetLabelResultData = new MatTableDataSource<KeyValue<string, any>>(
      result.filter((el) => el.value != null).sort((a, b) => a.key.localeCompare(b.key)),
    );
    this.loading = false;
  }

  openTable(data: TableComponentProps): void {
    this.dialog.open(DynamicTableDialogComponent, {
      data: { content: data },
    });
  }

  parseToTableProb(list: any[]): TableComponentProps {
    const keys: TableHeader[] = Object.keys(list[0]).map((x) => ({
      field: x,
      header: x,
    }));
    return {
      headers: keys,
      datTable: list,
    };
  }

  downloadExcel(): void {
    const s3Uri = this.assetLabel!.excelS3Uri;
    const download = s3Uri.split('/')[s3Uri.split('/').length - 1];
    this.productService.downloadSingleImage(s3Uri.split('//')[1]).subscribe((res) => {
      const blob: Blob = res.body as Blob;
      const a = document.createElement('a');
      const fileData = window.URL.createObjectURL(blob);
      a.href = fileData;
      a.download = download;
      a.click();
      window.URL.revokeObjectURL(fileData);
    });
  }

  extractAssetContent(): void {
    this.assetsService.extractLabelBoundingBox(this.assetDetail.id, this.assetType).subscribe({
      next: () => {
        this.alertService.successToast('Asset Content Queued for Extraction');
        this.contentExtracting = true;
      },
      error: (error: unknown) => console.error(error),
    });
  }

  openLabelEditor(): void {
    const currentLabel = this.assetLabelVersions[this.activeAssetLabelTab];
    const dialogRef = this.dialog.open(LabelEditorComponent, {
      width: '600px',
      data: {
        assetLabel: currentLabel,
        assetId: this.assetDetail.id,
        assetType: this.assetType,
      },
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.loadAssetLabel();
      }
    });
  }

  stringify(value: any): string {
    return JSON.stringify(value);
  }
}
